/**
 * useSurfaceSession — Surface Layer (/01)
 *
 * The real, session-backed auth hook. Replaces the mocked/preview login.
 *
 *   1. A single global session lifecycle (guarded so mounting the hook in
 *      multiple places — shell + sign-in page — never double-subscribes):
 *      restores a persisted session, keeps the synchronous access-token
 *      holder + the transport-agnostic `useAuthStore` in sync, and reacts
 *      to provider auth-state changes.
 *   2. Maps the provider user → the `Identity` contract.
 *   3. Exposes `signIn(email, password)` (password login only — private
 *      beta, no sign-up) and `signOut()`.
 *
 * When the provider env is absent (pure local design mode) a labelled
 * local preview session keeps the surface demoable without a backend.
 */

import { useCallback, useEffect } from 'react';
import type {
  AuthError,
  AuthErrorCode,
  Identity,
  IdentityRole,
} from '@contracts/identity.contract';
import type { UserThemePreference } from '@contracts/theme.contract';
import {
  getCurrentSession,
  isAuthConfigured,
  onAuthStateChange,
  redeemAuthRedirect,
  signInWithPassword,
  signOut as providerSignOut,
  type AuthSession,
  type AuthUser,
} from '@auth-provider';
import { useAuthStore } from './auth-store';
import { setAccessToken } from './session-token';
import { identityDefaultPlane } from '@lib/navigation/hubPlane';

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Map server-controlled `app_metadata.role`/membership → Surface roles. */
function mapRoles(appMetadata: Record<string, unknown>): IdentityRole[] {
  const raw = asString(appMetadata.tenant_role) ?? asString(appMetadata.role) ?? 'member';
  switch (raw) {
    case 'owner':
      return ['tenant.owner'];
    case 'admin':
      return ['tenant.admin'];
    case 'viewer':
      return ['tenant.viewer'];
    // developer / member / anything else → tenant member scope on the
    // public surface (control-plane roles are operator-only, never public).
    default:
      return ['tenant.member'];
  }
}

function defaultThemePreference(identityId: string): UserThemePreference {
  return {
    schemaVersion: 1,
    identityId,
    baseTheme: 'g100',
    tokens: {},
    surfaceTokens: { 'surface-density': 'comfortable' },
    updatedAt: new Date().toISOString(),
  };
}

/** Map a provider session user → the Surface `Identity` contract. */
function toIdentity(user: AuthUser): Identity {
  const tenantId = asString(user.appMetadata.active_tenant_id) ?? asString(user.appMetadata.tenant_id) ?? '';
  const displayLabel =
    asString(user.userMetadata.display_label) ??
    asString(user.userMetadata.display_name) ??
    asString(user.userMetadata.full_name) ??
    asString(user.userMetadata.name) ??
    user.email ??
    'Workspace user';
  const roles = mapRoles(user.appMetadata);
  return {
    id: user.id,
    email: user.email ?? '',
    displayLabel,
    tenantId,
    roles,
    themePreference: defaultThemePreference(user.id),
    // Land operators (DevHub-capable roles) on the control plane and members on
    // the tenant plane — derived from role, never hardcoded. The authoritative
    // plane is reconciled server-side (GET /api/me) and enforced by the BFF.
    defaultPlane: identityDefaultPlane(roles),
  };
}

function applySession(session: AuthSession | null): void {
  const store = useAuthStore.getState();
  if (session) {
    setAccessToken(session.accessToken);
    store.setIdentity(toIdentity(session.user));
  } else {
    setAccessToken(null);
    store.setIdentity(null);
  }
}

// One global lifecycle, regardless of how many components mount the hook.
let sessionLifecycleStarted = false;
function startSessionLifecycle(): void {
  if (sessionLifecycleStarted || !isAuthConfigured()) return;
  sessionLifecycleStarted = true;
  onAuthStateChange((_event, session) => applySession(session));
  // Magic-link redirect first: redeem the one-time hash payload for a real
  // session (or surface its error — e.g. an expired/already-used link);
  // otherwise restore the persisted session.
  void redeemAuthRedirect().then((redirect) => {
    if (redirect?.session) {
      applySession(redirect.session);
      return;
    }
    if (redirect?.error) {
      useAuthStore
        .getState()
        .setError(authError('invalid_credentials', redirect.error.message, true));
    }
    return getCurrentSession().then(applySession);
  });
}

function authError(code: AuthErrorCode, message: string, retryable = false): AuthError {
  return { code, message, retryable };
}

export type SurfaceSessionStatus = 'unauthenticated' | 'authenticated';

export interface SurfaceSession {
  /** True once the provider env is present (real login enforced). */
  configured: boolean;
  status: SurfaceSessionStatus;
  identity: Identity | null;
  email: string;
  error: AuthError | null;
  /** True when authenticated but no tenant resolved from the JWT yet. */
  tenantContextMissing: boolean;
  signIn: (email: string, password: string) => Promise<{ ok: boolean }>;
  signOut: () => Promise<void>;
}

/** Synthetic identity for local design mode (no provider configured). */
function previewIdentity(email: string): Identity {
  const normalized = email.trim() || 'preview@cestari.local';
  return {
    id: 'surface-preview-user',
    email: normalized,
    displayLabel: normalized,
    tenantId: 'surface-preview-tenant',
    roles: ['tenant.member'],
    themePreference: defaultThemePreference('surface-preview-user'),
    defaultPlane: 'tenant',
  };
}

export function useSurfaceSession(): SurfaceSession {
  const configured = isAuthConfigured();
  const identity = useAuthStore((s) => s.identity);
  const error = useAuthStore((s) => s.error);
  const setIdentity = useAuthStore((s) => s.setIdentity);
  const setError = useAuthStore((s) => s.setError);
  const setLoading = useAuthStore((s) => s.setLoading);
  const storeSignOut = useAuthStore((s) => s.signOut);

  useEffect(() => {
    startSessionLifecycle();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ ok: boolean }> => {
      setLoading(true);
      setError(null);
      try {
        if (!configured) {
          setIdentity(previewIdentity(email));
          return { ok: true };
        }
        const result = await signInWithPassword(email.trim(), password);
        if (result.error || !result.session) {
          const code: AuthErrorCode =
            result.error?.code === 'not_configured' ? 'not_configured' : 'invalid_credentials';
          setError(authError(code, result.error?.message ?? 'Email or password is incorrect.', true));
          return { ok: false };
        }
        applySession(result.session);
        const mapped = toIdentity(result.session.user);
        if (mapped.tenantId.length === 0) {
          setError(
            authError('tenant_context_missing', 'Signed in, but your workspace is still being provisioned.', true)
          );
        }
        return { ok: true };
      } catch (err) {
        setError(
          authError('network_unavailable', `Sign-in failed: ${(err as Error).message ?? 'network error'}`, true)
        );
        return { ok: false };
      } finally {
        setLoading(false);
      }
    },
    [configured, setIdentity, setError, setLoading]
  );

  const signOut = useCallback(async (): Promise<void> => {
    if (configured) {
      await providerSignOut();
    }
    setAccessToken(null);
    storeSignOut();
    // REQ-001.91 — after sign-out, redirect to the public landing ('/'), which on
    // app.cestari.studio is the public landing page. A hard navigation also
    // guarantees every in-memory authed view/state is cleared on logout.
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  }, [configured, storeSignOut]);

  return {
    configured,
    status: identity ? 'authenticated' : 'unauthenticated',
    identity,
    email: identity?.email ?? '',
    error,
    tenantContextMissing: Boolean(identity && identity.tenantId.length === 0),
    signIn,
    signOut,
  };
}
